import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import { Table, Button, Form, Input, Typography, Popover, Tooltip, Col, Row, Modal, message } from 'antd';
import {
    ArrowRightOutlined,
    CaretUpOutlined,
    CaretLeftOutlined,
    CaretRightOutlined,
    CaretDownOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import AccountEditing from '../AccountEditing';
import { fetchAllAccountsByNumber } from '../../utils/accountStructure/accountStructureUtils';
import { findAllAccounts } from '../../utils/accountStructure/accountStructureUtils';
import AccountConfigSummary from '../AccountConfigSummary';
import ResizableTitle from '../ResizeableTitle';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import TruncatedText from '../TruncatedText';
import ColumnSettings from '../ColumnSettings';
import RowContextMenu from '../RowContextMenu';
import { useSharedRowMenuItems } from '../../hooks/useSharedRowMenuItems';
import { useTableKeyboardNav } from '../../hooks/useTableKeyboardNav';
import { useLocateRow, LOCATE_HIT_CLASS } from '../../hooks/useLocateRow';
import './styles.scss';
import { getVisiblePeriods } from '../../utils/periods';

const EditableContext = React.createContext(null);
const { Text } = Typography;

const DEFAULT_WIDTHS = {
  account_number: 100,
  name: 170,
};

const SEPARATOR_TYPES = Object.freeze({
    SINGLE: 'single',
    DOUBLE: 'double',
    BLANK: 'blank',
    TEXT: 'text',
});

const isAccountSeparator = (row) =>
    row?.row_type === 'separator' || row?.separator_id != null;

const flattenRows = (rows, result = []) => {
    (rows ?? []).forEach((row) => {
        if (!row) return;

        result.push(row);

        if (Array.isArray(row.children)) {
            flattenRows(row.children, result);
        }
    });

    return result;
};

const getSeparatorRowClassName = (row) => {
    if (!isAccountSeparator(row)) return '';

     switch (row.separator_type) {
        case SEPARATOR_TYPES.DOUBLE:
            return 'account-separator-row account-separator-double';

        case SEPARATOR_TYPES.BLANK:
            return 'account-separator-row account-separator-blank';

        case SEPARATOR_TYPES.TEXT:
            return 'account-separator-row account-separator-text';

        case SEPARATOR_TYPES.SINGLE:
        default:
            return 'account-separator-row account-separator-single';
    }
};

const AccountTable = (props) => {
    const [widths, setWidth] = useColumnWidths('accountTable.columnWidths', DEFAULT_WIDTHS);
    const [isAccountNumberTaken, setIsAccountNumberTaken] = useState(false);
    const [formulaEditor, setFormulaEditor] = useState(null);
    const [rowContextMenu, setRowContextMenu] = useState({
        open: false,
        accountKey: null,
        x: 0,
        y: 0,
    });

     const promptForSeparatorText = ({
        title,
        initialValue = '',
        onSubmit,
    }) => {
        let textValue = initialValue;

        Modal.confirm({
            title,
            content: (
                <Input
                    autoFocus
                    defaultValue={initialValue}
                    maxLength={255}
                    placeholder="Enter heading text"
                    onChange={(event) => {
                        textValue =
                            event.target.value;
                    }}
                />
            ),
            okText: 'Save',
            cancelText: 'Cancel',
            onOk: () => {
                const normalizedText =
                    textValue.trim();

                if (!normalizedText) {
                    message.error(
                        'Enter text for the separator.',
                    );

                    return Promise.reject();
                }

                return onSubmit(
                    normalizedText,
                );
            },
        });
    };

 
    const { wrapperRef } = useTableKeyboardNav({
    onEnterEdit: (_r, _c, td) => {
        const wrap = td.querySelector('.editable-cell-value-wrap');
        wrap?.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
    },
    });

   const columnsInitial = [
    {
        title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                <span>Account</span>
                <ColumnSettings
                    columns={[
                        { key: 'account_number', label: 'Account' },
                        { key: 'name', label: 'Name' },
                    ]}
                    widths={widths}
                    onChange={setWidth}
                    defaults={DEFAULT_WIDTHS}
                />
            </div>
        ),
        dataIndex: 'account_number',
        key: 'account_number',
        fixed: 'left',
        width: widths.account_number,
        ellipsis: { showTitle: false },
        editable: true,
        render: (_, row) => {
            if (isAccountSeparator(row)) {
                return null;
            }

            const accountNumber =
                row.group_id === 5
                    ? row.account?.account_number
                    : row.account_number;

            return <TruncatedText>{accountNumber}</TruncatedText>;
        },
    },
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        width: widths.name,
        ellipsis: { showTitle: false },
        editable: true,
        
        render: (_, row) => {
            if (isAccountSeparator(row)) {
                if (
                    row.separator_type !==
                    SEPARATOR_TYPES.TEXT
                ) {
                    return null;
                }

                return (
                    <span className="account-separator-text-label">
                        {row.separator_text}
                    </span>
                );
            }
            const account = row.account ?? row;
            const carry = account.carry;
            const name = account.name;
            const convergence = account.convergence;

            return (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        marginLeft: 10,
                    }}
                >
                    <Popover
                        content={<AccountConfigSummary account={row} />}
                        title={null}
                        trigger="click"
                        placement="right"
                    >
                        <InfoCircleOutlined
                            style={{
                                color: '#bbb',
                                fontSize: 12,
                                cursor: 'pointer',
                                flexShrink: 0,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </Popover>

                    {carry && (
                        <Tooltip title="Carry — value carries forward to next period">
                            <ArrowRightOutlined
                                style={{
                                    color: '#1890ff',
                                    fontSize: 11,
                                    flexShrink: 0,
                                }}
                            />
                        </Tooltip>
                    )}

                    <div style={{ flex: 1, minWidth: 0 }}>
                        <TruncatedText>{name}</TruncatedText>
                    </div>

                    {convergence && (
                        <Tooltip title="Convergence — participates in iterative balancing">
                            <SyncOutlined
                                style={{
                                    color: '#52c41a',
                                    fontSize: 11,
                                    flexShrink: 0,
                                }}
                            />
                        </Tooltip>
                    )}
                </div>
            );
        },
    },
];

// Wire each column's onHeaderCell so the header receives the resize handler
  const resizableInitial = columnsInitial.map((col) => {
    if (typeof col.width !== 'number') return col;
    return {
      ...col,
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: (_, { size }) => {
          const next = Math.max(60, size.width);
          setWidth(col.key, next);
        },
      }),
    };
  });


    const allAccounts = useMemo(() => {
        if (!props.accountStructure) {
            return [];
        }

        const mainAccountStructure = {
            ...props.accountStructure,
        };

        // Custom tabs contain wrappers around existing accounts.
        // They should not be duplicated in the formula editor's account list.
        delete mainAccountStructure.custom;

        const collectedAccounts = [];

        findAllAccounts(
            collectedAccounts,
            mainAccountStructure,
        );

        // Defensive protection in case separators are part of the
        // account-structure response used by the UI.
        return collectedAccounts.filter(
            (row) => !isAccountSeparator(row),
        );
    }, [props.accountStructure]);

    const currentDisplayRows = useMemo(
        () => flattenRows(props.accounts ?? []),
        [props.accounts],
    );

    const setNewPeriodsCollapsed = (period, periods, expandedPeriods) => {
        let newPeriodsExpanded = [...expandedPeriods];

        for (let i = 0; i < periods.length; i++) {
            if (
                period.period_year === periods[i].period_year &&
                (periods[i].period_month !== '' || periods[i].period_quarter !== '')
            ) {
                newPeriodsExpanded[i] = false;
            }
        }
        props.handleSetExpandedPeriods(newPeriodsExpanded);
    };

    const setNewPeriodsExpanded = (period, isMonth) => {
        let newPeriodsExpanded = [...props.expandedPeriods];

        props.periods.forEach((p, index) => {
            let splitPeriod = p.period.split('-');

            if (!isMonth && splitPeriod.length === 2 && splitPeriod[0] === period.period && splitPeriod[1][0] === 'Q') {
                newPeriodsExpanded[index] = true;
            } else if (isMonth && splitPeriod.length === 2 && splitPeriod[0] === period.period) {
                newPeriodsExpanded[index] = true;
            }
        });
        props.handleSetExpandedPeriods(newPeriodsExpanded);
    };

    const renderExpansionButtons = (period) => {
        const currentPeriodIndex = props.periods.indexOf(period);
        const periodToExpand =
            props.periods[currentPeriodIndex - 1] != null ? props.periods[currentPeriodIndex - 1] : null;

        const showExpandMonth =
            periodToExpand != null
                ? periodToExpand.period_month !== ''
                : periodToExpand != null && periodToExpand.period_year === period.period_year
                  ? true
                  : null;

        const showExpandQuarter =
            periodToExpand != null
                ? periodToExpand.period_month === '' && periodToExpand.period_quarter !== ''
                : periodToExpand != null && periodToExpand.period_year === period.period_year
                  ? true
                  : null;

        const showCollapse = period != null ? period.period_month !== '' || period.period_quarter !== '' : false;

        return showExpandMonth || showExpandQuarter || showCollapse ? (
            <div>
                {showCollapse ? (
                    <Button
                        onClick={(e) => setNewPeriodsCollapsed(period, props.periods, props.expandedPeriods)}
                        type="text"
                    >
                        Collapse
                    </Button>
                ) : null}
                {showExpandMonth ? (
                    <Button onClick={() => setNewPeriodsExpanded(period, true)} type="text">
                        Expand to month
                    </Button>
                ) : null}
                {showExpandQuarter ? (
                    <Button onClick={() => setNewPeriodsExpanded(period, false)} type="text">
                        Expand to quarter
                    </Button>
                ) : null}
            </div>
        ) : null;
    };

    const periodColumn = (period, index, allPeriods = props.periods) => {
        const year = period.period.split('-')[0];
        const yearPeriod = allPeriods.find(p => p.period === year);
        const isParentYearForecast = yearPeriod?.forecast ?? false;
        
        return {
            title: (
                <Popover content={renderExpansionButtons(period, index)} title="">
                   <span style={{ color: isParentYearForecast ? 'blue' : 'black' }}>{period.period}</span>
                </Popover>
            ),
            dataIndex: `period${period.period}`,
            key: `period-${period.period}`,
            width: '150px',
            ellipsis: true,
            editable: true,
            render: (_, row) => {
                if (isAccountSeparator(row)) {
                    return null;
                }

                const scenarioAccount = row.account ?? row;

                const formula =
                    scenarioAccount.formulas?.find(
                        (item) =>
                            item.period === period.period,
                    )?.formula ?? '';

                if (formula === '') {
                    return null;
                }

                return (
                    <Tooltip
                        placement="top"
                        title={<span>{formula}</span>}
                    >
                        <span style={{ textAlign: 'right' }}>
                            {formula}
                        </span>
                    </Tooltip>
                );
            },
        };
    };
    const createColumns = (columnsInitial, periods, expandedPeriods) => {
       const newPeriods = getVisiblePeriods(periods, expandedPeriods);
        
        const all = [...columnsInitial, ...newPeriods.map((p, i) => periodColumn(p, i, periods))];
        return all.map((c, idx) => ({ ...c, colIndex: idx }));
    };
    
    const handleVerticalMove = (
        direction,
        event,
        row,
    ) => {
        event?.stopPropagation();

        if (!row) return;

        /*
        * Future unified movement handler.
        */
        const unifiedHandler =
            direction === 'up'
                ? props.handleMoveStructureRowUp
                : props.handleMoveStructureRowDown;

        if (typeof unifiedHandler === 'function') {
            unifiedHandler(event, row);
            return;
        }

        /*
        * Separators use the dedicated Redux/API movement flow.
        */
        if (isAccountSeparator(row)) {
            if (
                typeof props.handleMoveSeparator !==
                'function'
            ) {
                console.error(
                    'Missing handleMoveSeparator prop',
                );
                return;
            }

            props.handleMoveSeparator(
                row,
                direction,
            );

            return;
        }

        const isCustomAccount =
            row.custom_tab_accounts_id != null;

        const accountHandler =
            direction === 'up'
                ? isCustomAccount
                    ? props.handleMoveCustomAccountUp
                    : props.handleMoveAccountUp
                : isCustomAccount
                ? props.handleMoveCustomAccountDown
                : props.handleMoveAccountDown;

        /*
        * Custom-tab controllers expect the wrapper row.
        * Normal account controllers expect the underlying account.
        */
        const accountForMove = isCustomAccount
            ? row
            : row.account ?? row;

        accountHandler?.(
            event,
            accountForMove,
        );
    };

const renderMoveGrid = (row, closeMenu) => {
    const separator = isAccountSeparator(row);

    const isCustomAccount =
        !separator &&
        row?.custom_tab_accounts_id != null;

    const underlyingAccount = separator
        ? null
        : row?.account ?? row;

    const isAssociatedAccount =
        underlyingAccount?.associated_account_id != null;

    /*
     * Separators and custom-tab wrappers cannot be moved left/right.
     * They can only be moved vertically within their sibling list.
     */
    const canMoveLR =
        !separator &&
        !isCustomAccount &&
        underlyingAccount != null &&
        (
            isAssociatedAccount ||
            underlyingAccount.is_change_in !== true
        );

    return (
        <div style={{ marginLeft: '26px' }}>
            <Row>
                <Col span={24}>
                    <Button
                        style={{
                            marginLeft: '34px',
                            marginBottom: '3px',
                        }}
                        onClick={(event) => {
                            handleVerticalMove(
                                'up',
                                event,
                                row,
                            );
                        }}
                        size="small"
                    >
                        <CaretUpOutlined />
                    </Button>
                </Col>
            </Row>

            <Row style={{ width: '100px' }}>
                <Col span={8}>
                    {canMoveLR ? (
                        <Button
                            onClick={(event) => {
                                props.handleMoveAccountLeft?.(
                                    event,
                                    underlyingAccount,
                                );
                            }}
                            size="small"
                        >
                            <CaretLeftOutlined />
                        </Button>
                    ) : null}
                </Col>

                <Col span={8}>
                    <Button
                        style={{ marginLeft: '1px' }}
                        onClick={closeMenu}
                        size="small"
                    >
                        <CloseCircleOutlined />
                    </Button>
                </Col>

                <Col span={8}>
                    {canMoveLR ? (
                        <Button
                            style={{ marginLeft: '1px' }}
                            onClick={(event) => {
                                props.handleMoveAccountRight?.(
                                    event,
                                    underlyingAccount,
                                );
                            }}
                            size="small"
                        >
                            <CaretRightOutlined />
                        </Button>
                    ) : null}
                </Col>
            </Row>

            <Row>
                <Col span={24}>
                    <Button
                        style={{
                            marginLeft: '34px',
                            marginTop: '3px',
                        }}
                        onClick={(event) => {
                            handleVerticalMove(
                                'down',
                                event,
                                row,
                            );
                        }}
                        size="small"
                    >
                        <CaretDownOutlined />
                    </Button>
                </Col>
            </Row>
        </div>
    );
};

    const getAccountRowKey = (row) => {
        if (isAccountSeparator(row)) {
            return `separator:${row.separator_id}`;
        }

        if (row.custom_tab_accounts_id != null) {
            return `custom:${row.custom_tab_accounts_id}`;
        }

        if (row.associated_account_id != null) {
            return `associated:${row.associated_account_id}`;
        }

        return `account:${row.account_id}`;
    };
    const handleOpenMoveAccount = (e, accountId) => {
        e.preventDefault();
        
        const account = findAccountByRowKey(accountId);

        if (!account) {
        return;
    }

        setRowContextMenu({
            open: true,
            accountKey: accountId,
            x: e.clientX,
            y: e.clientY,
        });
    };
    
    const findAccountByRowKey = (rowKey) => {
        const selectedKey = Array.isArray(rowKey)
            ? rowKey[rowKey.length - 1]
            : rowKey;

        if (selectedKey == null) {
            return null;
        }

        const [type, rawId] = String(selectedKey).split(':');
        const id = Number(rawId);

        if (!Number.isInteger(id)) {
            return null;
        }

      const rowsToSearch = [
        ...currentDisplayRows,
        ...allAccounts,
    ];

        switch (type) {
            case 'separator':
                return (
                    rowsToSearch.find(
                        (row) =>
                            isAccountSeparator(row) &&
                            Number(row.separator_id) === id,
                    ) ?? null
                );

            case 'account':
                return (
                    rowsToSearch.find(
                        (row) =>
                            !isAccountSeparator(row) &&
                            row.custom_tab_accounts_id == null &&
                            row.associated_account_id == null &&
                            Number(row.account_id) === id,
                    ) ?? null
                );

            case 'associated':
                return (
                    rowsToSearch.find(
                        (row) =>
                            !isAccountSeparator(row) &&
                            Number(row.associated_account_id) === id,
                    ) ?? null
                );

            case 'custom':
                return (
                    rowsToSearch.find(
                        (row) =>
                            !isAccountSeparator(row) &&
                            Number(row.custom_tab_accounts_id) === id,
                    ) ?? null
                );

            default:
                return null;
        }
    };


    
    const EditableRow = ({ index, ...rowProps }) => {
        
        const [form] = Form.useForm();
        const rowKey = rowProps['data-row-key'] ?? null;

         const tableRow = (
                <tr
                    {...rowProps}
                    onContextMenu={(e) =>
                        handleOpenMoveAccount(e, rowKey)
                    }
                />
            );

            if (String(rowKey).startsWith('separator:')) {
                return tableRow;
            }


            return (
                <Form form={form} component={false}>
                    <EditableContext.Provider value={form}>
                        {tableRow}
                    </EditableContext.Provider>
                </Form>
            );
    };


    const EditableCell = ({ title, editable, children, dataIndex, account, handleSave, testing, ...restProps }) => {
        const [editing, setEditing] = useState(false);
        const inputRef = useRef(null);
        const skipNextBlur = useRef(false);
        const form = useContext(EditableContext);


        useEffect(() => {
            if (editing && inputRef && inputRef.current) {
                inputRef.current.focus();
            }
        }, [editing]);

        if (isAccountSeparator(account)) {
            return <td {...restProps}>{children}</td>;
        }

        if (account != null && account.associated_account_id != null) {
            editable = dataIndex !== 'name' && dataIndex !== 'account_number' ? false : true;
        }
       
        const handleBlur = (e) => {
            
            if (skipNextBlur.current) {
            skipNextBlur.current = false;
            return;
        }
        checkIsAccountTaken(e);
    };

        const toggleEdit = () => {
            const sourceAccount = account.account ?? account;

            setEditing((current) => !current);

            form.setFieldsValue({
                [dataIndex]: sourceAccount?.[dataIndex] ?? '',
            });
        };

        const checkIsAccountTaken = async (event) => {
            const accountNumber = event.target.value;
            let identicalAccounts = [];
            fetchAllAccountsByNumber(accountNumber, identicalAccounts, props.accountStructure);

            const zeroIdenticalAccountNumbers = identicalAccounts.length === 0;
            const identicalAccountNumber =
                identicalAccounts.length === 1 && identicalAccounts[0].account_number === account.account_number;

            const isAccountNumberTaken = !(zeroIdenticalAccountNumbers || identicalAccountNumber);

            if (!isAccountNumberTaken) {
                save();
            } else {
                form.setFieldsValue({
                    [dataIndex]: 'Account number taken',
                });
            }
        };

        const save = async () => {
            try {
                const values = await form.validateFields();

                if (!isAccountNumberTaken) {
                    toggleEdit();
                    handleSave(account, { ...account, ...values });
                }

                setIsAccountNumberTaken(false);
            } catch (errInfo) {
                // console.log("Save failed:", errInfo);
            }
        };

        let childNode = children;

        if (editable) {
            if (dataIndex.slice(0, 6) === 'period') {
                childNode = (
                    <div
                        className="editable-cell-value-wrap"
                        style={{
                            height: 24,
                            width: 150,
                            padding: 0,
                        }}
                        onClick={(e) => {
                            e.currentTarget.closest('td')?.focus();
                        }}
                        onDoubleClick={() => {
                            if (account.children?.length) return;

                            setFormulaEditor({
                            account,
                            period: dataIndex,
                            });
                        }}
                        >
                        {children}
                    </div>
                );
            } else {
                childNode = editing ? (
                    <Form.Item
                        style={{
                            margin: 0,
                        }}
                        name={dataIndex}
                        rules={[
                            {
                                required: true,
                                message: `${title} is required.`,
                            },
                        ]}
                    >
                        <Input ref={inputRef} 
                        onPressEnter={checkIsAccountTaken}
                        onBlur={handleBlur}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                e.stopPropagation();
                                skipNextBlur.current = true;
                                setEditing(false);
                                const td = e.target.closest('td');
                                setTimeout(() => td?.focus(), 0);
                            }
                         }} 
                         />
                    </Form.Item>
                ) : (
                    <div
                        className="editable-cell-value-wrap"
                        style={{
                            paddingLeft: 10,
                            paddingBottom: 3,
                        }}
                        onClick={(e) => {
                            e.currentTarget.closest('td')?.focus();
                        }}
                        onDoubleClick={toggleEdit}
                    >
                        {children}
                    </div>
                );
            }
        }

        return (
            <td {...restProps}>
                
                {childNode}
            </td>
        );
    };

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    };

    const handleSave = (account, row) => {
        if (props.handleUpdateAccount) props.handleUpdateAccount(account, row);
    };

    const editableColumns = createColumns(resizableInitial, props.periods, props.expandedPeriods).map((col) => {
        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (row, rowIndex) => {
                if (isAccountSeparator(row)) {
                    const isTextNameCell =
                        row.separator_type ===
                            SEPARATOR_TYPES.TEXT &&
                        col.dataIndex === 'name';

                    return {
                        account: row,
                        editable: false,
                        dataIndex: col.dataIndex,
                        title: col.title,
                        tabIndex: -1,

                        className: [
                            'account-separator-cell',
                            isTextNameCell
                                ? 'account-separator-text-cell'
                                : '',
                        ]
                            .filter(Boolean)
                            .join(' '),
                    };
                }

                return {
                    account: row,
                    editable: col.editable,
                    dataIndex: col.dataIndex,
                    title: col.title,
                    handleSave,
                    'data-cell-row': rowIndex,
                    'data-cell-col': col.colIndex,
                    tabIndex: 0,
                };
            },
        };
    });

    
  const onSelectedRowKeysChange = (rowKeys) => {
    const selectedRowKey = rowKeys[rowKeys.length - 1];

    if (selectedRowKey == null) {
        props.setSelectedRowKey([]);
        props.handleSetSelectedAccount(null);
        return;
    }

    const selectedRow = findAccountByRowKey(selectedRowKey);

    if (!selectedRow || isAccountSeparator(selectedRow)) {
        props.setSelectedRowKey([]);
        props.handleSetSelectedAccount(null);
        return;
    }

    props.setSelectedRowKey([selectedRowKey]);
    props.handleSetSelectedAccount(selectedRow);
};

    const rowSelection = {
        selectedRowKeys: props.selectedRowKey,
        onChange: onSelectedRowKeysChange,

        getCheckboxProps: (row) => ({
            disabled: isAccountSeparator(row),
        }),

        renderCell: (checked, row, index, originNode) =>
            isAccountSeparator(row) ? null : originNode,
    };

    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    const { highlightKey } = useLocateRow({
        focusRequest: props.focusRequest,
        rows: props.accounts,
        getKey: getAccountRowKey,
        containerRef: wrapperRef,
        onExpand: (keys) =>
            setExpandedRowKeys((prev) => Array.from(new Set([...prev, ...keys]))),
        // Select it too, so the Account Management button acts on it.
        onFound: (key) => onSelectedRowKeysChange([key]),
    });

    const getRowClassName = (row) =>
        getAccountRowKey(row) === highlightKey
            ? `${getSeparatorRowClassName(row)} ${LOCATE_HIT_CLASS}`
            : getSeparatorRowClassName(row);

    const rowMenuAccount = findAccountByRowKey(rowContextMenu.accountKey);

    const rowMenuIsSeparator =
    isAccountSeparator(rowMenuAccount);

    const rowSharedItems = useSharedRowMenuItems({
        account: rowMenuIsSeparator ? null : rowMenuAccount,
        callbacks: {
            onOpenAccountManagement:
                props.onOpenAccountManagement ??
                props.handlers?.onOpenAccountManagement,
            onShowWhereUsed: props.onShowWhereUsed,
        },
    });

    const createSeparator = (
        position,
        separatorType,
        separatorText = null,
    ) => {
        if (!rowMenuAccount || rowMenuIsSeparator) {
            return;
        }

        if (
            typeof props.handleCreateSeparator !==
            'function'
        ) {
            console.error(
                'Missing handleCreateSeparator prop',
            );
            return;
        }

        const underlyingAccount =
            rowMenuAccount.account ?? rowMenuAccount;

        const anchorType =
            rowMenuAccount.custom_tab_accounts_id != null
                ? 'custom'
                : underlyingAccount.associated_account_id != null
                ? 'associated'
                : 'account';

        const anchorId =
            anchorType === 'custom'
                ? rowMenuAccount.custom_tab_accounts_id
                : anchorType === 'associated'
                ? underlyingAccount.associated_account_id
                : underlyingAccount.account_id;

        props.handleCreateSeparator({
            anchorType,
            anchorId,
            position,
            separatorType,
            separatorText,
        });

        setRowContextMenu((previous) => ({
            ...previous,
            open: false,
        }));
    };

    const updateSeparatorType = (separatorType,  separatorText = null) => {
        if (!rowMenuIsSeparator) return;

        if (
        typeof props.handleUpdateSeparator !==
        'function'
    ) {
        console.error(
            'Missing handleUpdateSeparator prop',
        );
        return;
    }

        props.handleUpdateSeparator?.(
            rowMenuAccount,
            separatorType,
            separatorText,
        );
          setRowContextMenu((previous) => ({
    ...previous,
    open: false,
  }));
};

   const accountRowContextMenuItems = [
    {
        key: 'move',
        render: (closeMenu) =>
            renderMoveGrid(
                rowMenuAccount,
                closeMenu,
            ),
    },
    {
        key: 'div-move',
        divider: true,
    },
    {
        key: 'insert-separator-above',
        label: 'Insert separator above',
        children: [
            {
                key: 'separator-single-above',
                label: 'Single line',
                onClick: () =>
                    createSeparator(
                        'before',
                        SEPARATOR_TYPES.SINGLE,
                    ),
            },
            {
                key: 'separator-double-above',
                label: 'Double line',
                onClick: () =>
                    createSeparator(
                        'before',
                        SEPARATOR_TYPES.DOUBLE,
                    ),
            },
            {
                key: 'separator-blank-above',
                label: 'Empty block',
                onClick: () =>
                    createSeparator(
                        'before',
                        SEPARATOR_TYPES.BLANK,
                    ),
            },
            {
                key: 'separator-text-above',
                label: 'Text heading',
                onClick: () =>
                    promptForSeparatorText({
                        title:
                            'Insert text heading above',
                        onSubmit: (text) =>
                            createSeparator(
                                'before',
                                SEPARATOR_TYPES.TEXT,
                                text,
                            ),
                    }),
            },
        ],
    },

    {
        key: 'insert-separator-below',
        label: 'Insert separator below',
        children: [
            {
                key: 'separator-single-below',
                label: 'Single line',
                onClick: () =>
                    createSeparator(
                        'after',
                        SEPARATOR_TYPES.SINGLE,
                    ),
            },
            {
                key: 'separator-double-below',
                label: 'Double line',
                onClick: () =>
                    createSeparator(
                        'after',
                        SEPARATOR_TYPES.DOUBLE,
                    ),
            },
            {
                key: 'separator-blank-below',
                label: 'Empty block',
                onClick: () =>
                    createSeparator(
                        'after',
                        SEPARATOR_TYPES.BLANK,
                    ),
            },
            
            {
                key: 'separator-text-below',
                label: 'Text heading',
                onClick: () =>
                    promptForSeparatorText({
                        title:
                            'Insert text heading below',
                        onSubmit: (text) =>
                            createSeparator(
                                'after',
                                SEPARATOR_TYPES.TEXT,
                                text,
                            ),
                    }),
            },
        ],
    },

    {
        key: 'div-shared',
        divider: true,
        visible: rowSharedItems.length > 0,
    },
    ...rowSharedItems,
];

const separatorRowContextMenuItems = [
    {
        key: 'move',
        render: (closeMenu) =>
            renderMoveGrid(
                rowMenuAccount,
                closeMenu,
            ),
    },
    {
        key: 'div-move',
        divider: true,
    },
    {
        key: 'separator-type',
        label: 'Separator type',
        children: [
            {
                key: 'type-single',
                label: 'Single line',
                disabled:
                    rowMenuAccount?.separator_type ===
                    SEPARATOR_TYPES.SINGLE,
                onClick: () =>
                    updateSeparatorType(
                        SEPARATOR_TYPES.SINGLE,
                    ),
            },
            {
                key: 'type-double',
                label: 'Double line',
                disabled:
                    rowMenuAccount?.separator_type ===
                    SEPARATOR_TYPES.DOUBLE,
                onClick: () =>
                    updateSeparatorType(
                        SEPARATOR_TYPES.DOUBLE,
                    ),
            },
            {
                key: 'type-blank',
                label: 'Empty block',
                disabled:
                    rowMenuAccount?.separator_type ===
                    SEPARATOR_TYPES.BLANK,
                onClick: () =>
                    updateSeparatorType(
                        SEPARATOR_TYPES.BLANK,
                    ),
            },
            {
                key: 'type-text',
                label: 'Text heading',
                disabled:
                    rowMenuAccount?.separator_type ===
                    SEPARATOR_TYPES.TEXT,
                onClick: () =>
                    promptForSeparatorText({
                        title:
                            'Change to text heading',
                        initialValue:
                            rowMenuAccount
                                ?.separator_text ?? '',
                        onSubmit: (text) =>
                            updateSeparatorType(
                                SEPARATOR_TYPES.TEXT,
                                text,
                            ),
                    }),
            },
        ],
    },
    {
        key: 'edit-separator-text',
        label: 'Edit text',
        visible:
            rowMenuAccount?.separator_type ===
            SEPARATOR_TYPES.TEXT,
        onClick: () =>
            promptForSeparatorText({
                title: 'Edit heading text',
                initialValue:
                    rowMenuAccount
                        ?.separator_text ?? '',
                onSubmit: (text) =>
                    updateSeparatorType(
                        SEPARATOR_TYPES.TEXT,
                        text,
                    ),
            }),
    },
    {
        key: 'div-delete',
        divider: true,
    },
    {
        key: 'delete-separator',
        label: 'Delete separator',
        danger: true,
        onClick: () => {
            if (!rowMenuIsSeparator) {
                return;
            }

            if (
                typeof props.handleDeleteSeparator !==
                'function'
            ) {
                console.error(
                    'Missing handleDeleteSeparator prop in AccountTable',
                );
                return;
            }

            props.handleDeleteSeparator(
                rowMenuAccount,
            );

            setRowContextMenu((previous) => ({
                ...previous,
                open: false,
            }));
        },
    },
];

const rowContextMenuItems =
    rowMenuIsSeparator
        ? separatorRowContextMenuItems
        : accountRowContextMenuItems;

    return (
    <>
    <Table
        ref={wrapperRef}
        rowKey={getAccountRowKey}
        rowClassName={getRowClassName}
        expandable={{
            expandedRowKeys,
            onExpandedRowsChange: setExpandedRowKeys,
        }}
        components={{
            header: { cell: ResizableTitle },
            body: {
                row: EditableRow,
                cell: EditableCell,
            },
        }}
        columns={editableColumns}
        bordered
        rowSelection={{
            hideSelectAll: true,
            ...rowSelection,
        }}
        dataSource={props.accounts}
        sticky={{ offsetScroll: 0 }}
        pagination={false}
        size="small"
        className="tableFooter"
        scroll={{ x: 1300 }}
    />

    <RowContextMenu
        open={rowContextMenu.open}
        onOpenChange={(open) => {
            setRowContextMenu((prev) => ({ ...prev, open }));
        }}
        title={<div style={{ textAlign: 'center' }}>Row actions</div>}
        items={rowContextMenuItems}
        getPopupContainer={() => document.body}
        >
    <div
        style={{
        position: 'fixed',
        left: rowContextMenu.x,
        top: rowContextMenu.y,
        width: 1,
        height: 1,
        pointerEvents: 'none',
        zIndex: 9999,
        }}
    />
    </RowContextMenu>

    {formulaEditor ? (
      <AccountEditing
        show={true}
        account={formulaEditor.account}
        allAccounts={allAccounts}
        period={formulaEditor.period}
        accountStructure={props.accountStructure}
        handleClose={() => {
            setFormulaEditor(null);
        }}
      />
    ) : null}
  </>
  );
};

export default AccountTable;
